import React, { useCallback } from 'react';

import Configs from './Configs';
import * as apiCalls from './common/apiCalls';

// Common
import { ErrorBoundaryShell } from './common/errorBoundaryShell.jsx';

// -----------------------------------------------------------------------------
// Fallback: full reload so it works when Router is not mounted (root ErrorBoundary).
// ErrorBoundaryShell reads personalization via DataProvider (parent must wrap DataProvider).
// -----------------------------------------------------------------------------
function ErrorBoundaryFallback() {
  const handleGoHome = useCallback(() => {
    window.location.assign('/');
  }, []);

  return <ErrorBoundaryShell onGoHome={handleGoHome} />;
}

// -----------------------------------------------------------------------------
// ErrorBoundary
// -----------------------------------------------------------------------------
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.unhandledRejectionHandler = this.unhandledRejectionHandler.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  unhandledRejectionHandler(event) {
    console.error(
      '[ErrorBoundary.js][unhandledrejection()] Unhandled promise rejection:',
      event.reason
    );

    apiCalls.logErrorToBackend(import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN, {
      frontendService: Configs.devEnvironment
        ? 'frontend_dev'
        : Configs.stagingEnvironment
          ? 'frontend_staging'
          : 'frontend_prod',
      type: 'error',
      message: event.reason?.toString(),
      stack: event.reason?.toString(),
      componentStack: event.reason?.toString(),
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      location: window.location.href,
    });
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary.js][componentDidCatch()] Error: ', error, errorInfo);

    apiCalls.logErrorToBackend(import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN, {
      frontendService: Configs.devEnvironment
        ? 'frontend_dev'
        : Configs.stagingEnvironment
          ? 'frontend_staging'
          : 'frontend_prod',
      type: 'error',
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      location: window.location.href,
    });
  }

  componentDidMount() {
    window.onerror = (message, source, lineno, colno, error) => {
      console.error('[ErrorBoundary.js][window.onerror()] Global error caught:', {
        message,
        source,
        lineno,
        colno,
        error,
      });

      apiCalls.logErrorToBackend(import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN, {
        frontendService: Configs.devEnvironment
          ? 'frontend_dev'
          : Configs.stagingEnvironment
            ? 'frontend_staging'
            : 'frontend_prod',
        type: 'error',
        message: message,
        stack: error?.stack,
        componentStack: error?.toString(),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        location: window.location.href,
        lineno: lineno,
        colno: colno,
        source: source,
      });
    };

    window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);
  }

  componentWillUnmount() {
    window.onerror = null;
    window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorBoundaryFallback />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
